import React, { useState, useEffect } from 'react';
import { channelApi, sarinahApi } from '../config/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { FiRefreshCw, FiTag, FiSearch, FiLayers, FiGrid, FiUser, FiInfo, FiCheck, FiPackage } from 'react-icons/fi';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import PublishedProductsTab from './PublishedProductsTab';

export default function TicketMonitor({ memberCode }) {
  const [transactions, setTransactions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedVoucher, setSelectedVoucher] = useState(null);
  const [coupons, setCoupons] = useState([]);
  const [couponLoading, setCouponLoading] = useState(false);
  const { user } = useAuth();
  const isRetail = memberCode === 'retail';

  const fetchTransactions = async () => {
    setIsLoading(true);
    try {
      const params = {};
      if (memberCode) {
        // Map 'ap' → 'api' untuk backend compatibility (backend uses 'api' for Angkasa Pura)
        const codeMap = { 'ap': 'api' };
        params.member_code = codeMap[memberCode] || memberCode;
      } else if (user?.admin_status === 'admin_member' && user?.organization_code) {
        params.member_code = user.organization_code.toLowerCase();
      }
      const response = await channelApi.get('/admin/transactions', { params });
      if (response.data?.status === 200) {
        setTransactions(response.data.data || []);
      }
    } catch (err) {
      console.error(err);
      toast.error('Gagal memuat data monitoring tiket.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, [user?.organization_code, user?.admin_status, memberCode]);

  const fetchCoupons = async () => {
    setCouponLoading(true);
    try {
      const res = await sarinahApi.get('/coupons/monitor');
      if (res.data?.status === 200) setCoupons(res.data.data || []);
    } catch (err) {
      console.error(err);
      toast.error('Gagal memuat data kupon Sarinah.');
    } finally {
      setCouponLoading(false);
    }
  };

  useEffect(() => {
    if (isRetail) fetchCoupons();
  }, [isRetail]);

  const couponStatusColor = (s) => {
    if (s === 'REDEEMED') return 'bg-purple-500/10 text-purple-600';
    if (s === 'DELIVERED' || s === 'ISSUED') return 'bg-emerald-500/10 text-emerald-600';
    if (s === 'PENDING' || s === 'PROCESSING') return 'bg-amber-500/10 text-amber-600';
    if (['DEAD', 'UNFULFILLED', 'REVOKED', 'EXPIRED'].includes(s)) return 'bg-rose-500/10 text-rose-600';
    return 'bg-slate-500/10 text-slate-600';
  };

  // Flat-map bookings to show individual tickets/vouchers
  const tickets = React.useMemo(() => {
    const list = [];
    transactions.forEach(tx => {
      if (tx.supplier_notification_status && Array.isArray(tx.supplier_notification_status)) {
        tx.supplier_notification_status.forEach(sup => {
          list.push({
            id: sup.voucherCode || 'PENDING',
            componentName: sup.componentName,
            category: sup.category || 'Voucher',
            supplierName: sup.supplierName,
            status: sup.status,
            qrData: sup.qrData,
            qrImage: sup.qrImage,
            dispatchedAt: sup.dispatchedAt,
            // Parent booking fields
            bookingId: tx.id,
            invoiceCode: tx.invoiceCode,
            packageName: tx.packageName,
            travelDate: tx.travelDate,
            passengers: tx.passengers || [],
            channelName: tx.channel_name
          });
        });
      }
    });
    return list;
  }, [transactions]);

  // Filter logic
  const filteredTickets = tickets.filter(t => {
    const matchesSearch = 
      t.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.componentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.packageName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.bookingId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.passengers.some(p => p.name.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesSearch;
  });

  return (
    <div className="flex flex-col gap-6">
      
      {/* Header action bar */}
      <div className="flex flex-col justify-between gap-4 md:flex-row md:items-center">
        <div>
          <h1 className="text-2xl font-black tracking-tight text-slate-800">
            {memberCode
              ? `Monitoring Tiket - ${memberCode.toUpperCase()}`
              : user?.admin_status === 'admin_member'
                ? `Monitoring Tiket - ${String(user?.organization_code || '').toUpperCase()}`
                : 'Monitoring Tiket & Voucher Terbit (Holding)'}
          </h1>
          <p className="text-sm text-slate-500">
            {memberCode
              ? `Pantau tiket dan voucher digital untuk organisasi ${memberCode.toUpperCase()} secara real-time.`
              : user?.admin_status === 'admin_member'
                ? 'Kelola dan verifikasi tiket/voucher digital yang terbit untuk komponen organisasi Anda secara real-time.'
                : 'Pantau seluruh tiket digital, kamar hotel, lounge, dan transfer jeep yang terbit dari transaksi omnichannel.'}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchTransactions} className="gap-2 self-start md:self-auto">
          <FiRefreshCw className={isLoading ? 'animate-spin' : ''} />
          Refresh Tiket
        </Button>
      </div>

      <Tabs defaultValue="transactions" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="published" className="gap-1.5"><FiPackage className="h-3.5 w-3.5" /> Published Products</TabsTrigger>
          <TabsTrigger value="transactions" className="gap-1.5"><FiTag className="h-3.5 w-3.5" /> Transaction Tickets</TabsTrigger>
          {isRetail && (
            <TabsTrigger value="coupons" className="gap-1.5"><FiTag className="h-3.5 w-3.5" /> Kupon Sarinah</TabsTrigger>
          )}
        </TabsList>
        <TabsContent value="published"><PublishedProductsTab memberCode={memberCode} /></TabsContent>
        <TabsContent value="transactions">

      {/* Analytics widgets */}
      <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
        <Card className="border-0 bg-white shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="rounded-xl bg-[#EFF6FF] p-3 text-[#2563EB]">
                <FiTag className="h-5 w-5" />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Total Tiket Terbit</p>
                <h3 className="text-2xl font-bold text-slate-800">{tickets.length} Voucher</h3>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 bg-white shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="rounded-xl bg-[#ECFDF5] p-3 text-[#059669]">
                <FiLayers className="h-5 w-5" />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Supplier Terkoneksi</p>
                <h3 className="text-2xl font-bold text-slate-800">
                  {(() => {
                    const suppliers = new Set();
                    tickets.forEach(t => { if (t.supplierName) suppliers.add(t.supplierName); });
                    return suppliers.size;
                  })()} Aktif
                </h3>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 bg-white shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="rounded-xl bg-purple-50 p-3 text-purple-600">
                <FiGrid className="h-5 w-5" />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Cakupan Keanggotaan</p>
                <h3 className="text-lg font-bold text-slate-800 truncate max-w-[200px]">
                  {memberCode
                    ? memberCode.toUpperCase()
                    : user?.admin_status === 'admin_member'
                      ? (user?.organization_name || String(user?.organization_code || '').toUpperCase())
                      : 'Semua Organisasi Holding'}
                </h3>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter and Table Card */}
      <Card className="border-0 bg-white shadow-sm">
        <div className="p-6 pb-4">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <h2 className="text-base font-bold text-slate-700">Daftar Status Tiket/Voucher Digital</h2>
            
            <div className="flex items-center gap-3">
              {/* Search Bar */}
              <div className="relative">
                <FiSearch className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input 
                  type="text"
                  placeholder="Cari kode tiket, tamu, booking..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="rounded-lg border border-slate-200 pl-9 pr-4 py-1.5 text-xs focus:border-brand-500 focus:outline-none w-[280px]"
                />
              </div>
            </div>
          </div>
        </div>
        <div className="px-6 pb-6">
          <Table>
            <TableHeader className="bg-slate-50">
              <TableRow>
                <TableHead className="font-bold w-[130px]">Kode Voucher</TableHead>
                <TableHead className="font-bold">Layanan Komponen</TableHead>
                <TableHead className="font-bold">Kategori / Supplier</TableHead>
                <TableHead className="font-bold">Kode Booking Utama</TableHead>
                <TableHead className="font-bold">Tanggal Perjalanan</TableHead>
                <TableHead className="font-bold">Daftar Wisatawan</TableHead>
                <TableHead className="font-bold text-center">Status Hub</TableHead>
                <TableHead className="font-bold text-center w-[80px]">QR</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={8} className="py-8 text-center text-slate-400">Memuat data tiket...</TableCell>
                </TableRow>
              ) : filteredTickets.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="py-8 text-center text-slate-400">Tidak ada tiket/voucher yang ditemukan.</TableCell>
                </TableRow>
              ) : (
                filteredTickets.map((ticket, i) => (
                  <TableRow key={i} className="hover:bg-slate-50/50">
                    <TableCell className="font-bold text-slate-800">{ticket.id}</TableCell>
                    <TableCell className="font-semibold text-slate-700 text-xs">
                      {ticket.componentName}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-semibold text-slate-700 text-[11px]">{ticket.category}</span>
                        <span className="text-[9px] text-slate-400 font-medium">{ticket.supplierName}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-bold text-slate-800 text-[11px]">{ticket.bookingId}</span>
                        <span className="text-[9px] font-mono text-slate-400">{ticket.invoiceCode}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-slate-600 font-medium">
                      {new Date(ticket.travelDate).toLocaleDateString('id-ID', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-0.5">
                        {ticket.passengers.map((p, idx) => (
                          <span key={idx} className="text-[10px] text-slate-600 font-semibold truncate max-w-[120px] block">
                            • {p.name}
                          </span>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge className="text-[9px] bg-emerald-500/10 text-emerald-600 border-0">
                        {ticket.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-8 w-8 text-brand-500 hover:bg-brand-50"
                        onClick={() => setSelectedVoucher(ticket)}
                      >
                        <FiGrid className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Ticket Details & QR Code Dialog */}
      <Dialog open={!!selectedVoucher} onOpenChange={() => setSelectedVoucher(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-slate-800 font-black">Detail Tiket & Validasi E-Voucher</DialogTitle>
          </DialogHeader>
          {selectedVoucher && (
            <div className="flex flex-col items-center gap-5 pt-2">
              {/* Simulated QR Code card */}
              <div className="flex flex-col items-center border border-slate-100 rounded-2xl p-6 bg-slate-50/50 shadow-sm w-full">
                <div className="h-40 w-40 bg-white border border-slate-200 rounded-xl p-3 flex flex-col items-center justify-center relative shadow-inner">
                  {selectedVoucher.qrImage ? (
                    <img src={selectedVoucher.qrImage} alt="QR Code" className="w-full h-full object-contain" />
                  ) : (
                    <div className="w-full h-full border-4 border-slate-800 flex items-center justify-center font-black text-slate-800 text-center text-xs tracking-widest break-all p-1">
                      [QR VALIDATION BARCODE IMAGE]
                    </div>
                  )}
                </div>
                <span className="text-xs font-mono text-slate-400 mt-4 tracking-wide font-bold">{selectedVoucher.qrData}</span>
                <Badge className="mt-2 bg-[#E0F2FE] text-[#0369A1] hover:bg-[#E0F2FE] border-0 text-[10px] gap-1 px-2.5 py-0.5 font-bold">
                  <FiCheck className="h-3 w-3" /> QR Validated by Hub
                </Badge>
              </div>

              {/* Passenger and ticket details */}
              <div className="w-full flex flex-col gap-3.5 text-xs">
                <div className="grid grid-cols-2 border-b border-slate-100 pb-2">
                  <span className="text-slate-400 font-semibold">Nomor Voucher</span>
                  <span className="text-slate-800 font-bold text-right">{selectedVoucher.id}</span>
                </div>
                <div className="grid grid-cols-2 border-b border-slate-100 pb-2">
                  <span className="text-slate-400 font-semibold">Nama Komponen</span>
                  <span className="text-slate-800 font-bold text-right truncate max-w-[200px]">{selectedVoucher.componentName}</span>
                </div>
                <div className="grid grid-cols-2 border-b border-slate-100 pb-2">
                  <span className="text-slate-400 font-semibold">Penyedia Jasa (Supplier)</span>
                  <span className="text-slate-800 font-bold text-right">{selectedVoucher.supplierName}</span>
                </div>
                <div className="grid grid-cols-2 border-b border-slate-100 pb-2">
                  <span className="text-slate-400 font-semibold">Tanggal Berangkat</span>
                  <span className="text-slate-800 font-bold text-right">
                    {new Date(selectedVoucher.travelDate).toLocaleDateString('id-ID', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                  </span>
                </div>

                {/* Tourists details */}
                <div className="flex flex-col gap-2 mt-2 bg-slate-50/50 rounded-xl p-3 border border-slate-100">
                  <span className="text-slate-400 font-bold uppercase tracking-wider text-[10px]">Daftar Penumpang/Tamu:</span>
                  {selectedVoucher.passengers.map((p, idx) => (
                    <div key={idx} className="flex justify-between items-center text-[11px] font-semibold text-slate-700">
                      <span className="flex items-center gap-1.5">
                        <FiUser className="text-slate-400" />
                        {p.name}
                      </span>
                      <span className="font-mono text-slate-400">ID: {p.identityNumber}</span>
                    </div>
                  ))}
                </div>

                <div className="flex items-start gap-2 text-[10px] text-slate-400 bg-amber-50/40 rounded-xl p-3 border border-amber-100/50 mt-1">
                  <FiInfo className="text-amber-500 shrink-0 mt-0.5 h-3.5 w-3.5" />
                  <p>Tiket terbit ini terintegrasi langsung dengan API Gateway maskapai/hotel/destinasi PRIME. Penumpang dapat menggunakan kode QR atau nomor voucher saat check-in fisik di counter.</p>
                </div>
              </div>

              <Button onClick={() => setSelectedVoucher(null)} className="w-full bg-slate-900 hover:bg-slate-800 mt-2 font-bold text-xs h-9">
                Tutup E-Voucher
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

        </TabsContent>

        {isRetail && (
        <TabsContent value="coupons">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-base font-bold text-slate-700">Kupon Sarinah Terbit</h2>
              <p className="text-xs text-slate-500">Status klaim kupon Sarinah dari transaksi Retail secara real-time.</p>
            </div>
            <Button variant="outline" size="sm" onClick={fetchCoupons} className="gap-2">
              <FiRefreshCw className={couponLoading ? 'animate-spin' : ''} />
              Refresh Kupon
            </Button>
          </div>
          <Card className="border-0 bg-white shadow-sm">
            <div className="px-6 py-4">
              <Table>
                <TableHeader className="bg-slate-50">
                  <TableRow>
                    <TableHead className="font-bold w-[150px]">Kode Kupon</TableHead>
                    <TableHead className="font-bold">Transaksi</TableHead>
                    <TableHead className="font-bold">Customer</TableHead>
                    <TableHead className="font-bold text-center">Status</TableHead>
                    <TableHead className="font-bold">Dibuat</TableHead>
                    <TableHead className="font-bold">Keterangan</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {couponLoading ? (
                    <TableRow><TableCell colSpan={6} className="py-8 text-center text-slate-400">Memuat data kupon...</TableCell></TableRow>
                  ) : coupons.length === 0 ? (
                    <TableRow><TableCell colSpan={6} className="py-8 text-center text-slate-400">Belum ada kupon yang terbit.</TableCell></TableRow>
                  ) : (
                    coupons.map((c) => (
                      <TableRow key={c.id} className="hover:bg-slate-50/50">
                        <TableCell className="font-bold text-slate-800 font-mono text-xs">{c.coupon_code || '—'}</TableCell>
                        <TableCell className="text-xs text-slate-600 font-medium">{c.transaction_id}</TableCell>
                        <TableCell className="text-xs text-slate-600">{c.customer_id || '—'}</TableCell>
                        <TableCell className="text-center">
                          <Badge className={`text-[9px] border-0 ${couponStatusColor(c.status)}`}>{c.status}</Badge>
                        </TableCell>
                        <TableCell className="text-xs text-slate-500">
                          {c.created_at ? new Date(c.created_at).toLocaleString('id-ID', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                        </TableCell>
                        <TableCell className="text-[10px] text-rose-500 max-w-[200px] truncate">{c.error_message || ''}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </Card>
        </TabsContent>
        )}
      </Tabs>

    </div>
  );
}
